
import { useState, useEffect } from "react";
import { VideoInput } from "./VideoInput";
import { ChatInterface } from "../chat/ChatInterface";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { ChatLoadingState } from "../chat/ChatLoadingState";

interface VideoWorkspaceProps {
  hasSubmittedUrl: boolean;
  videoId: string | null;
  onUrlSubmit: (submitted: boolean, videoId: string) => void;
  showChatByDefault?: boolean;
}

export const VideoWorkspace = ({ 
  hasSubmittedUrl, 
  videoId, 
  onUrlSubmit,
  showChatByDefault = false 
}: VideoWorkspaceProps) => {
  const [showChat, setShowChat] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (showChatByDefault && videoId) {
      setShowChat(true);
    }
  }, [showChatByDefault, videoId]);

  const handleNewVideo = () => {
    onUrlSubmit(false, "");
    setShowChat(false);
    setIsProcessing(false);
  };

  const handleUrlSubmit = async (submitted: boolean, id: string, isProcessed?: boolean) => {
    setShowChat(submitted); // Show chat immediately when URL is submitted
    setIsProcessing(submitted && !isProcessed);
    onUrlSubmit(submitted, id);
  };

  if (!hasSubmittedUrl || !videoId) {
    return (
      <div className="max-w-2xl mx-auto w-full px-4 md:px-8 space-y-8 animate-fade-in">
        <div className="text-center space-y-4">
          <h1 className="text-2xl md:text-3xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Enhance Your Learning Experience
          </h1>
          <p className="text-muted-foreground">
            Enter a YouTube URL to start exploring content with AI-powered insights
          </p>
        </div>
        <VideoInput onUrlSubmit={handleUrlSubmit} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full px-4 md:px-8 animate-fade-in">
      <div className="space-y-6">
        <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-card">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="flex justify-center">
          <Button
            onClick={handleNewVideo}
            variant="outline"
            className="transition-all duration-300 hover:scale-105 hover:bg-primary/10 gap-2 min-h-[44px]"
          >
            <Upload className="h-4 w-4" />
            New Video
          </Button>
        </div>
      </div>
      <div className="animate-fade-in">
        {showChat && (
          isProcessing ? (
            <ChatLoadingState message="Processing video and preparing chat..." />
          ) : (
            <ChatInterface videoId={videoId} />
          )
        )}
      </div>
    </div>
  );
};
